import MirrorBackButton from "@/components/MirrorBackButton";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import PreviewNotice from "@/components/PreviewNotice";
import { Plus, ArrowRight, ChevronLeft, BookOpen, GraduationCap, Headphones, FileText, Star, ChevronDown, ChevronUp } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/i18n/LanguageProvider";

// ── Types ──────────────────────────────────────────────────────────────────

type ItemFilter   = "All" | "Books" | "Courses" | "Podcasts" | "Articles";
type ItemType     = "Book" | "Course" | "Podcast" | "Article";
type ItemStatus   = "Reading" | "Completed" | "Paused" | "Wishlist";

interface LearningItem {
  id: string;
  title: string;
  author: string;
  type: ItemType;
  status: ItemStatus;
  progress: number; // 0–100
  completedDate?: string;
}

// ── Static data ────────────────────────────────────────────────────────────

const FILTERS: ItemFilter[] = ["All", "Books", "Courses", "Podcasts", "Articles"];
const FILTER_KEYS: Record<ItemFilter, string> = { All: "learning.filter.all", Books: "learning.filter.books", Courses: "learning.filter.courses", Podcasts: "learning.filter.podcasts", Articles: "learning.filter.articles" };
const STATUS_KEYS: Record<ItemStatus, string> = { Reading: "learning.status.reading", Completed: "learning.status.completed", Paused: "learning.status.paused", Wishlist: "learning.status.wishlist" };
const TYPE_KEYS: Record<ItemType, string> = { Book: "learning.type.book", Course: "learning.type.course", Podcast: "learning.type.podcast", Article: "learning.type.article" };

const TYPE_FILTER_MAP: Record<ItemType, ItemFilter> = {
  Book:    "Books",
  Course:  "Courses",
  Podcast: "Podcasts",
  Article: "Articles",
};

const TYPE_ICON: Record<ItemType, React.ReactNode> = {
  Book:    <BookOpen    size={16} style={{ color: "hsl(187 72% 55%)" }} />,
  Course:  <GraduationCap size={16} style={{ color: "hsl(265 70% 72%)" }} />,
  Podcast: <Headphones  size={16} style={{ color: "hsl(45 90% 60%)" }}  />,
  Article: <FileText    size={16} style={{ color: "hsl(142 55% 55%)" }}  />,
};

const STATUS_STYLE: Record<ItemStatus, { bg: string; text: string; border: string }> = {
  Reading:   { bg: "hsl(187 72% 50% / 0.12)", text: "hsl(187 72% 60%)",  border: "hsl(187 72% 50% / 0.25)" },
  Completed: { bg: "hsl(142 60% 35% / 0.18)", text: "hsl(142 55% 55%)",  border: "hsl(142 55% 40% / 0.30)" },
  Paused:    { bg: "hsl(45 80% 40% / 0.18)",  text: "hsl(45 90% 60%)",   border: "hsl(45 80% 45% / 0.30)"  },
  Wishlist:  { bg: "hsl(220 9% 46% / 0.15)",  text: "hsl(218 14% 65%)",  border: "hsl(220 9% 46% / 0.25)"  },
};

const TYPE_BGS: Record<ItemType, string> = {
  Book:    "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&q=80",
  Course:  "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&q=80",
  Podcast: "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=400&q=80",
  Article: "https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&q=80",
};

const ITEMS: LearningItem[] = [
  { id: "1", title: "Atomic Habits",         author: "James Clear",           type: "Book",    status: "Reading",   progress: 67 },
  { id: "2", title: "Deep Work",             author: "Cal Newport",           type: "Book",    status: "Completed", progress: 100, completedDate: "Mar 10" },
  { id: "3", title: "Huberman Lab Podcast",  author: "Andrew Huberman",       type: "Podcast", status: "Paused",    progress: 40 },
  { id: "4", title: "The Lean Startup",      author: "Eric Ries",             type: "Book",    status: "Wishlist",  progress: 0  },
  { id: "5", title: "React Advanced Course", author: "Frontend Masters",      type: "Course",  status: "Reading",   progress: 23 },
];

const COMPLETED_ITEMS: LearningItem[] = [
  { id: "c1", title: "Deep Work",          author: "Cal Newport",      type: "Book",    status: "Completed", progress: 100, completedDate: "Mar 10" },
  { id: "c2", title: "The 4-Hour Work Week", author: "Tim Ferriss",   type: "Book",    status: "Completed", progress: 100, completedDate: "Feb 22" },
  { id: "c3", title: "Designing Your Life",  author: "Bill Burnett",  type: "Book",    status: "Completed", progress: 100, completedDate: "Jan 15" },
];

// ── Page ───────────────────────────────────────────────────────────────────

const Learning: React.FC = () => {
  const navigate  = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [filter, setFilter]     = useState<ItemFilter>("All");
  const [expanded, setExpanded] = useState<string | null>(null);

  const stagger = (i: number) => ({ delay: 0.06 + i * 0.08, duration: 0.5 });

  const visible = filter === "All"
    ? ITEMS
    : ITEMS.filter((it) => TYPE_FILTER_MAP[it.type] === filter);

  const inProgress = ITEMS.filter((it) => it.status === "Reading" || it.status === "Paused").length;
  const wishlist   = ITEMS.filter((it) => it.status === "Wishlist").length;

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      <PreviewNotice />
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[380px] h-[220px] rounded-full opacity-[0.04]"
          style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }}
        />
      </div>

      {/* BACK BUTTON */}
      <MirrorBackButton className="mb-4" />

      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between mb-5"
      >
        <div>
          <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5"
            style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("learning.subtitle")}</p>
          <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">
            {t("learning.title")}
          </h1>
        </div>
        <button
          onClick={() => toast({ description: t("common.comingSoon"), duration: 2000 })}
          className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300"
          style={{ background: "hsl(187 72% 50% / 0.10)", border: "1px solid hsl(187 72% 50% / 0.20)" }}
        >
          <Plus size={16} style={{ color: "hsl(187 72% 50%)" }} />
        </button>
      </motion.div>

      {/* STATS */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(0.5)}
        className="grid grid-cols-3 gap-2.5 mb-5"
      >
        {[
          { label: t("learning.thisYear"),  value: "8" },
          { label: t("learning.inProgress"), value: String(inProgress) },
          { label: t("learning.wishlist"),   value: String(wishlist) },
        ].map((s) => (
          <div key={s.label} className="rounded-xl p-3 text-center"
            style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.14)" }}>
            <p className="font-display text-[20px] font-bold text-foreground leading-none">{s.value}</p>
            <p className="text-[9px] font-body mt-1" style={{ color: "hsl(218 14% 50%)" }}>{s.label}</p>
          </div>
        ))}
      </motion.div>

      {/* CURRENTLY READING */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(1)}
        className="rounded-2xl p-5 mb-5 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #0d1a30 60%, #161628 100%)", border: "1.5px solid hsl(187 72% 50% / 0.35)" }}
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
        <div
          className="absolute top-3 right-3 w-[80px] h-[100px] rounded-xl opacity-50"
          style={{ background: "linear-gradient(145deg, hsl(265 60% 35%), hsl(187 60% 25%))" }}
        />
        <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-3"
          style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("learning.currentlyReading")}</p>
        <div className="flex items-start gap-3 relative z-10">
          <div
            className="w-[56px] h-[80px] rounded-xl flex-shrink-0 flex items-center justify-center"
            style={{ background: "linear-gradient(145deg, hsl(265 60% 40%), hsl(187 60% 30%))" }}
          >
            <BookOpen size={22} style={{ color: "hsl(187 72% 70%)" }} />
          </div>
          <div className="flex-1 min-w-0">
            <span className="inline-block px-2 py-0.5 rounded-full font-display text-[9px] font-semibold mb-2"
              style={{ background: "hsl(187 72% 50% / 0.14)", color: "hsl(187 72% 60%)", border: "1px solid hsl(187 72% 50% / 0.28)" }}>
              {t("learning.type.book")}
            </span>
            <h2 className="font-display text-[18px] font-bold text-foreground leading-tight mb-0.5">
              Atomic Habits
            </h2>
            <p className="text-[11px] font-body mb-3" style={{ color: "hsl(218 14% 55%)" }}>
              James Clear
            </p>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "hsl(218 14% 20%)" }}>
                <div className="h-full rounded-full" style={{ width: "67%", background: "hsl(var(--primary))" }} />
              </div>
              <span className="text-[10px] font-display font-bold" style={{ color: "hsl(var(--primary))" }}>67%</span>
            </div>
            <button
              onClick={() => toast({ description: t("common.comingSoon"), duration: 2000 })}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl font-display text-[11px] font-semibold tracking-wide active:scale-[0.97] transition-transform"
              style={{ background: "hsl(var(--primary))", color: "hsl(var(--background))" }}
            >
              {t("learning.continue")}
            </button>
          </div>
        </div>
      </motion.div>

      {/* FILTERS */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={stagger(1.5)}
        className="flex gap-2 mb-5 overflow-x-auto pb-1"
        style={{ scrollbarWidth: "none" }}
      >
        {FILTERS.map((f) => {
          const active = filter === f;
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className="shrink-0 px-4 py-1.5 rounded-full font-display text-[11px] tracking-wide transition-all duration-200 active:scale-95"
              style={
                active
                  ? { background: "hsl(var(--primary))", color: "hsl(var(--background))", fontWeight: 600 }
                  : { background: "hsl(187 72% 50% / 0.07)", border: "1px solid hsl(187 72% 50% / 0.15)", color: "hsl(210 20% 60%)" }
              }
            >
              {t(FILTER_KEYS[f])}
            </button>
          );
        })}
      </motion.div>

      {/* ITEM LIST */}
      <div className="space-y-2.5 mb-5">
        {visible.map((item, i) => {
          const st = STATUS_STYLE[item.status];
          const isOpen = expanded === item.id;
          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04 * i }}
              className="rounded-2xl overflow-hidden hover:scale-[1.02] hover:brightness-110 transition-all"
              style={{ background: `linear-gradient(rgba(0,0,0,0.52) 0%, rgba(0,0,0,0.72) 100%), url(${TYPE_BGS[item.type]}) center/cover no-repeat`, border: "1px solid rgba(0,229,255,0.22)", filter: "saturate(1.15)" }}
            >
              <div className="flex items-center gap-3 p-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "hsl(187 72% 50% / 0.08)", border: "1px solid hsl(187 72% 50% / 0.15)" }}>
                  {TYPE_ICON[item.type]}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <p className="font-display text-[13px] font-semibold text-foreground">{item.title}</p>
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-display tracking-wide"
                      style={{ background: st.bg, color: st.text, border: `1px solid ${st.border}` }}>
                      {t(STATUS_KEYS[item.status])}
                    </span>
                  </div>
                  <p className="text-[10px] font-body mb-2" style={{ color: "hsl(218 14% 50%)" }}>
                    {item.author}
                  </p>
                  {item.progress > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: "hsl(218 14% 20%)" }}>
                        <div className="h-full rounded-full"
                          style={{ width: `${item.progress}%`, background: item.status === "Completed" ? "hsl(142 55% 45%)" : "#00e5ff" }} />
                      </div>
                      <span className="text-[9px] font-display font-semibold"
                        style={{ color: item.status === "Completed" ? "hsl(142 55% 55%)" : "hsl(187 72% 60%)" }}>
                        {item.progress}%
                      </span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setExpanded(isOpen ? null : item.id)}
                  className="flex items-center gap-1 shrink-0 font-display text-[10px] tracking-wide transition-opacity opacity-60 hover:opacity-100"
                  style={{ color: "hsl(187 72% 55%)" }}
                >
                  <span>{t("learning.insights")}</span>
                  {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
              </div>

              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 border-t" style={{ borderColor: "hsl(187 72% 50% / 0.12)" }}>
                      <p className="text-[9px] font-display tracking-[0.14em] uppercase mt-3 mb-2"
                        style={{ color: "hsl(218 14% 40%)" }}>{t("learning.keyInsights")}</p>
                      <textarea
                        placeholder={t("learning.insightsPlaceholder")}
                        rows={3}
                        className="w-full bg-transparent text-[11px] font-body text-foreground/65 placeholder:text-foreground/20 focus:outline-none resize-none"
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {/* COMPLETED */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(4)}
        className="rounded-2xl p-4 mb-5"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.16)" }}
      >
        <p className="text-[10px] font-display tracking-[0.16em] uppercase mb-3"
          style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("learning.completed")}</p>
        <div className="space-y-2.5">
          {COMPLETED_ITEMS.map((item) => (
            <div key={item.id} className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: "hsl(142 60% 35% / 0.18)", border: "1px solid hsl(142 55% 40% / 0.28)" }}>
                {TYPE_ICON[item.type]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-[12px] font-semibold text-foreground/80">{item.title}</p>
                <p className="text-[10px] font-body" style={{ color: "hsl(218 14% 45%)" }}>{item.author}</p>
              </div>
              <span className="text-[9px] font-display tracking-wide shrink-0"
                style={{ color: "hsl(218 14% 40%)" }}>{item.completedDate}</span>
              <span className="px-2 py-0.5 rounded-full text-[9px] font-display font-semibold shrink-0"
                style={{ background: "hsl(142 60% 35% / 0.18)", color: "hsl(142 55% 55%)", border: "1px solid hsl(142 55% 40% / 0.28)" }}>
                ✓
              </span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* MIRROR INSIGHT */}
      {/* HIDDEN: fake Mirror Insight — re-wire with real Padrão B per audit 23/04 (memory #23) */}
      {false && (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(5)}
        className="rounded-2xl p-4 mb-5 relative overflow-hidden"
        style={{ background: "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.65)), url(https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_012.jpeg) center/cover no-repeat", border: "1px solid hsl(187 72% 50% / 0.22)" }}
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/25 to-transparent" />
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.20)" }}>
            <Star size={14} style={{ color: "hsl(187 72% 55%)" }} />
          </div>
          <div className="flex-1">
            <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-1"
              style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("learning.mirrorInsight")}</p>
            <p className="text-[12px] font-body text-foreground/65 leading-relaxed mb-3">
              {t("learning.mirrorInsightText")}
            </p>
            <button
              onClick={() => navigate("/mirror")}
              className="flex items-center gap-1 font-display text-[11px] font-medium tracking-wide opacity-75 hover:opacity-100 transition-opacity"
              style={{ color: "hsl(187 72% 55%)" }}
            >
              <span>{t("learning.exploreWithMirror")}</span>
              <ArrowRight size={11} />
            </button>
          </div>
        </div>
      </motion.div>
      )}

    </MobileLayout>
  );
};

export default Learning;
